// Updated: accept .webp/.png/.jpg uploads up to 20MB, auto-convert to WebP at 80% quality; active upload feedback via spinner label and optimistic preview
import React, { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { Image, Loader2 } from 'lucide-react';
import Collapsible from '../components/Collapsible';
import RichTextEditor from '../../RichTextEditor';
import { useToastContext } from '../../../contexts/ToastContext';
import { validateImageFile, convertToWebp } from '../../../utils/imageUpload';

const MembershipSection = () => {
  const [mainImage, setMainImage] = useState('');
  const [previewImage, setPreviewImage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [texts, setTexts] = useState({
    main_text: '',
    secondary_text: ''
  });
  const [editorKeys, setEditorKeys] = useState({ main: 0, secondary: 0 });
  const { addToast } = useToastContext();

  const uploadImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) {
      return;
    }

    const rawFile = event.target.files[0];
    const validation = validateImageFile(rawFile);
    if (!validation.ok) {
      addToast(validation.message, 'error');
      event.target.value = '';
      return;
    }

    const objectUrl = URL.createObjectURL(rawFile);
    setPreviewImage(objectUrl);
    setUploading(true);

    try {
      const file = await convertToWebp(rawFile);

      // Delete old image if exists
      if (mainImage) {
        const oldFilePath = mainImage.split('/').pop();
        if (oldFilePath) {
          await supabase.storage
            .from('images')
            .remove([`membership/${oldFilePath}`]);
        }
      }

      const fileName = `membership-${Date.now()}.webp`;
      const filePath = `membership/${fileName}`;

      // Upload new image
      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);

      // Update settings
      const { error: updateError } = await supabase
        .from('settings')
        .upsert({ 
          key: 'membership_main_image',
          value: publicUrl
        }, {
          onConflict: 'key'
        });

      if (updateError) throw updateError;

      setMainImage(publicUrl);
      addToast('Afbeelding succesvol bijgewerkt', 'success');
    } catch (error) {
      console.error('Error uploading image:', error);
      addToast('Er is een fout opgetreden bij het uploaden van de afbeelding', 'error');
    } finally {
      URL.revokeObjectURL(objectUrl);
      setPreviewImage('');
      setUploading(false);
      event.target.value = '';
    }
  };

  useEffect(() => {
    fetchImages();
    fetchTexts();
  }, []);

  const fetchTexts = async () => {
    try {
      const { data: mainText } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'membership_main_text')
        .single();

      const { data: secondaryText } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'membership_secondary_text')
        .single();

      const newMainText = mainText?.value || '';
      const newSecondaryText = secondaryText?.value || '';

      setTexts({
        main_text: newMainText,
        secondary_text: newSecondaryText
      });
      setEditorKeys(prev => ({
        main: prev.main + 1,
        secondary: prev.secondary + 1
      }));
    } catch (error) {
      console.error('Error fetching membership texts:', error);
    }
  };

  const updateText = async (key: 'membership_main_text' | 'membership_secondary_text', value: string) => {
    try {
      const { error } = await supabase
        .from('settings')
        .upsert({ 
          key,
          value
        }, {
          onConflict: 'key'
        });

      if (error) throw error;
      addToast('Tekst succesvol bijgewerkt', 'success');
    } catch (error) {
      console.error('Error updating text:', error);
      addToast('Er is een fout opgetreden bij het bijwerken van de tekst', 'error');
    }
  };

  const fetchImages = async () => {
    try {
      const { data, error } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'membership_main_image')
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      setMainImage(data?.value || '');
    } catch (error) {
      console.error('Error fetching membership images:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateImage = async (key: 'membership_main_image' | 'membership_secondary_image', value: string) => {
    if (!value.toLowerCase().endsWith('.webp')) {
      addToast('Alleen .webp afbeeldingen zijn toegestaan', 'error');
      return;
    }

    try {
      const { error } = await supabase
        .from('settings')
        .upsert({ 
          key,
          value
        }, {
          onConflict: 'key'
        });

      if (error) throw error;
      
      setMainImage(value);
      addToast('Afbeelding succesvol bijgewerkt', 'success');
    } catch (error) {
      console.error('Error updating image:', error);
      addToast('Er is een fout opgetreden bij het bijwerken van de afbeelding', 'error');
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="space-y-6">
        <Collapsible title="Hoofdtekst aanpassen">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hoofdtekst
              </label>
              <div className="space-y-4">
                <RichTextEditor
                  key={editorKeys.main}
                  content={texts.main_text}
                  onChange={(value) => setTexts(prev => ({ ...prev, main_text: value }))}
                />
                <button
                  onClick={() => updateText('membership_main_text', texts.main_text)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#E37C64] hover:bg-[#5A130E]"
                >
                  Tekst aanpassen
                </button>
              </div>
            </div>
          </div>
        </Collapsible>

        <Collapsible title="Secundaire tekst aanpassen">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Secundaire tekst
              </label>
              <div className="space-y-4">
                <RichTextEditor
                  key={editorKeys.secondary}
                  content={texts.secondary_text}
                  onChange={(value) => setTexts(prev => ({ ...prev, secondary_text: value }))}
                />
                <button
                  onClick={() => updateText('membership_secondary_text', texts.secondary_text)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#E37C64] hover:bg-[#5A130E]"
                >
                  Tekst aanpassen
                </button>
              </div>
            </div>
          </div>
        </Collapsible>

        <Collapsible title="Afbeelding aanpassen">
          <div>
            <div className="flex gap-4 items-end">
              <label className={`flex-1 inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white ${uploading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}`}>
                <input
                  type="file"
                  accept="image/webp,image/png,image/jpeg"
                  onChange={uploadImage}
                  disabled={uploading}
                  className="hidden"
                />
                {uploading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Bezig met uploaden...</>
                ) : (
                  'Kies bestand'
                )}
              </label>
            </div>
            {(previewImage || mainImage) && (
              <div className="mt-4 relative w-64 h-64">
                <img
                  src={previewImage || mainImage}
                  alt="Main membership section"
                  className={`w-64 h-64 object-cover rounded-lg transition-opacity ${uploading ? 'opacity-70' : ''}`}
                />
                {uploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg">
                    <Loader2 className="w-8 h-8 text-white animate-spin" />
                  </div>
                )}
              </div>
            )}
          </div>
        </Collapsible>
      </div>
    </div>
  );
};

export default MembershipSection;